/* eslint-disable sonarjs/cognitive-complexity */
import {
  useFormState,
  useStateObject,
  isObjectEmptyData,
} from 'holistic-resources';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DefaultActionButtons from 'components/DefaultActionButtons';
import Modal from 'components/Modal';
import DateField from 'components/formFields/DateField';
import SelectField from 'components/formFields/SelectField';
import TextField from 'components/formFields/TextField';
import SectionLabel from 'components/typography/SectionLabel';
import SubSectionLabel from 'components/typography/SubSectionLabel';
import {
  showSuccessFeedbackBar,
  showErrorFeedbackBar,
  setSelectedBooking,
  setLatestBookings,
} from 'reducers/ui/uiSlice';
import { updateClientApi, createClientApi, updatePassengerApis } from 'services/ClientService';
import { GENDER_OPTIONS, COUNTRY_CODE_TO_NAME } from 'shared/constants/common';
import {
  UPDATE_ERROR,
  UPDATE_SUCCESS,
} from 'shared/constants/feedbackMessages';
import { getValue } from 'shared/helpers/common';

const initializeForm = (isOpen, isNewPassport, data, setFormValue) => {
  if (isOpen && !isNewPassport) {
    setFormValue(data);
  }
};

const renderFullnameSection = (fullname, componentConfig) => {
  if (fullname && componentConfig.bookingPassportsUpdateEnabled) {
    return (
      <>
        <SectionLabel label={fullname} />
        <div className="text-gray-500 text-sm">
          Edit {fullname} passport information below:{' '}
        </div>
      </>
    );
  }
  return null;
};

export default function PassportModal({
  isOpen,
  setModalState,
  onPassportUpdate,
  data = {},
  fullname = '',
  formDropdownConfig = {
    countryOfResidence: [],
    nationality: [],
    issueLocation: [],
  },
  isNewPassport = false,
  visitor = {},
  passportType,
  bookingRefOrId = false,
  isCruiseBooking = false,
}) {
  const dispatch = useDispatch();

  const { componentConfig, latestBookings, selectedBooking } = useSelector(
    (state) => state.ui,
  );

  const isPassengerFieldDisabled = passportType === "passenger" && !componentConfig.bookingPassportsUpdateEnabled;

  const { formValue, setFormValue, formChangeHandler } = useFormState({});
  const { value: formErrorMessages, updateValue: setFormErrorMessages } =
    useStateObject({
      passportExpiry: '',
      passportIssue: '',
      dateOfBirth: '',
    });

  const closeModal = () => {
    setModalState(false);
  };

  const onChangeName = ({ target: { name, value } } = {}) => {
    setFormValue((current) => ({
      ...current,
      name: {
        ...current.name,
        [name]: value,
      },
    }));
  };

  const updateReduxBookings = (refOrId, formData) => {
    const updatedBooking = { ...selectedBooking };
    updatedBooking.passengers = updatedBooking.passengers.map((passenger) =>
      passenger.id === formData.id ? { ...passenger, ...formData } : passenger
    );

    let updatedLatestBookings = latestBookings;
    updatedLatestBookings = updatedLatestBookings.map((booking) =>
      booking.id === refOrId || booking.reference === refOrId ? updatedBooking : booking
    );

    dispatch(setSelectedBooking(updatedBooking));
    dispatch(setLatestBookings(updatedLatestBookings));
  }

  const updatePassport = async (type, refOrId) => {
    try {
      if (type === "client") {
        const { id } = formValue;
        const { data: updatedPassport } = await updateClientApi(id, formValue);
        onPassportUpdate?.(id, updatedPassport);
        dispatch(showSuccessFeedbackBar(UPDATE_SUCCESS));
        closeModal();
      }
      else if (type === "passenger") {
        const { data: updatedPassport } = await updatePassengerApis(refOrId, [formValue]);
        const { id } = formValue;
        onPassportUpdate?.(id, updatedPassport);
        updateReduxBookings(refOrId, formValue);
        dispatch(showSuccessFeedbackBar(UPDATE_SUCCESS));
        closeModal();
      }
    } catch (error) {
      console.error(error);
      dispatch(showErrorFeedbackBar(UPDATE_ERROR));
    }
  };

  const createPassport = async () => {
    try {
      await createClientApi({...formValue, visitor});
      window.location.reload();
      dispatch(showSuccessFeedbackBar(UPDATE_SUCCESS));
      closeModal();
    } catch (error) {
      console.error(error);
      dispatch(showErrorFeedbackBar(UPDATE_ERROR));
    }
  };

  const {
    passportNumber,
    passportIssueLocation,
    passportIssue,
    passportExpiry,
    countryOfResidence,
    citizenship,
    dateOfBirth,
    destinationAddress,
    gender,
    title,
    firstName,
    middleName,
    lastName,
    name: { first, last, middle } = {},
    nextOfKinFirstName,
    nextOfKinLastName,
    nextOfKinPhone,
    nextOfKinEmail,
    nextOfKinRelationship,
    noInsurance,
    insuranceCompanyName,
    insurancePolicyNumber,
    insuranceTelephone,
    insuranceValidFrom,
    insuranceValidTo,
  } = formValue;

  const [noInsuranceState, setNoInsuranceState] = useState(noInsurance);

  const handleNoInsuranceChange = (event) => {
    const isChecked = event.target.checked;
    setNoInsuranceState(isChecked);

    if (isChecked) {
      setFormValue((prev) => ({
        ...prev,
        noInsurance: true,
        insuranceCompanyName: '',
        insurancePolicyNumber: '',
        insuranceTelephone: '',
        insuranceValidFrom: null,
        insuranceValidTo: null,
      }));
    } else {
      setFormValue((prev) => ({
        ...prev,
        noInsurance: false,
      }));
    }
  };

  const customTextFieldStyles = 'width: 47% !important;';

  const issueLocationCodes = getValue(formDropdownConfig?.issueLocation, []).map((item) => {
    return typeof item === "object" && item.value ? item.value : item;
  });

  const transformedIssueLocationOptions = issueLocationCodes.map((code) => ({
    value: code,
    label: COUNTRY_CODE_TO_NAME[code] || code,
  }));

  useEffect(() => {
    initializeForm(isOpen, isNewPassport, data, setFormValue);
  }, [data, setFormValue, isOpen, isNewPassport]);

  useEffect(() => {
    setNoInsuranceState(noInsurance);
  }, [noInsurance]);

  return (
    <Modal
      show={isOpen}
      onClose={() => setModalState(false)}
      header="View / Edit passport details"
      body={
        <>
          {renderFullnameSection(fullname, componentConfig)}
          <SubSectionLabel className="mt-6" label="Personal details" />
          <div className="flex flex-wrap gap-x-4 w-full justify-between">

            {passportType === "passenger" ? (
              <>
                <TextField
                  label="Title"
                  name="title"
                  value={title}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled
                />
                <TextField
                  label="First name"
                  name="firstName"
                  value={firstName}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled
                />
                <TextField
                  label="Middle name"
                  name="middleName"
                  value={middleName}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled
                />
                <TextField
                  label="Last name"
                  name="lastName"
                  value={lastName}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled
                />
              </>
            ) : (
              <>
                <TextField
                  label="First name"
                  name="first"
                  value={first}
                  onChange={onChangeName}
                  customStyles={customTextFieldStyles}
                />
                <TextField
                  label="Middle name"
                  name="middle"
                  value={middle}
                  onChange={onChangeName}
                  customStyles={customTextFieldStyles}
                />
                <TextField
                  label="Last name"
                  name="last"
                  value={last}
                  onChange={onChangeName}
                  customStyles={customTextFieldStyles}
                />
              </>
            )}

            <SelectField
              label="Country of residence"
              name="countryOfResidence"
              options={getValue(formDropdownConfig?.countryOfResidence, [])}
              value={countryOfResidence}
              onChange={formChangeHandler}
              customStyles={customTextFieldStyles}
              containerClassName="w-full"
              disabled={isPassengerFieldDisabled}
            />
            <SelectField
              label="Citizenship"
              name="citizenship"
              options={getValue(formDropdownConfig?.nationality, [])}
              value={citizenship}
              onChange={formChangeHandler}
              customStyles={customTextFieldStyles}
              containerClassName="w-full"
              disabled={isPassengerFieldDisabled}
            />
            <div className="w-[47%] my-2">
              <label htmlFor="passportIssue" className="block text-gray-500 my-1">
                Date of birth
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={dateOfBirth ? new Date(dateOfBirth).toISOString().split('T')[0] : ''}
                onChange={(e) => formChangeHandler({ target: { name: 'dateOfBirth', value: new Date(e.target.value).getTime() } })}
                className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isPassengerFieldDisabled}
              />
            </div>

          </div>
          <SubSectionLabel className="mt-6" label="Passport details" />
          <div className="flex flex-wrap gap-x-4 w-full justify-between">
            <TextField
              label="Passport number"
              name="passportNumber"
              value={passportNumber}
              onChange={formChangeHandler}
              customStyles={customTextFieldStyles}
              disabled={isPassengerFieldDisabled}
            />
            <SelectField
              label="Passport issue location"
              name="passportIssueLocation"
              options={transformedIssueLocationOptions}
              value={passportIssueLocation}
              onChange={formChangeHandler}
              customStyles={customTextFieldStyles}
              containerClassName="w-full"
              disabled={isPassengerFieldDisabled}
            />

            <div className="w-[47%] my-2">
              <label htmlFor="passportIssue" className="block text-gray-500 my-1">
                Passport issue date
              </label>
              <input
                type="date"
                id="passportIssue"
                name="passportIssue"
                value={passportIssue ? new Date(passportIssue).toISOString().split('T')[0] : ''}
                onChange={(e) => formChangeHandler({ target: { name: 'passportIssue', value: new Date(e.target.value).getTime() } })}
                className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isPassengerFieldDisabled}
              />
            </div>

            <div className="w-[47%] my-2">
              <label htmlFor="passportExpiry" className="block text-gray-500 my-1">
                Passport expiry date
              </label>
              <input
                type="date"
                id="passportExpiry"
                name="passportExpiry"
                value={passportExpiry ? new Date(passportExpiry).toISOString().split('T')[0] : ''}
                onChange={(e) => formChangeHandler({ target: { name: 'passportExpiry', value: new Date(e.target.value).getTime() } })}
                className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isPassengerFieldDisabled}
              />
            </div>

            <SelectField
              label="Gender"
              name="gender"
              options={GENDER_OPTIONS}
              value={gender}
              onChange={formChangeHandler}
              customStyles={customTextFieldStyles}
              errorMessage={formErrorMessages?.gender}
              setFormErrorMessages={setFormErrorMessages}
              disabled={isPassengerFieldDisabled}
            />
          </div>
          {passportType === "passenger" && (
            <>
              <SubSectionLabel className="mt-6" label="Next of kin details" />
              <div className="flex flex-wrap gap-x-4 w-full justify-between">
                <TextField
                  label="Next of kin first name"
                  name="nextOfKinFirstName"
                  value={nextOfKinFirstName}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled={!componentConfig.bookingPassportsUpdateEnabled}
                />
                <TextField
                  label="Next of kin last name"
                  name="nextOfKinLastName"
                  value={nextOfKinLastName}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled={!componentConfig.bookingPassportsUpdateEnabled}
                />
                <TextField
                  label="Next of kin phone number"
                  name="nextOfKinPhone"
                  value={nextOfKinPhone}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled={!componentConfig.bookingPassportsUpdateEnabled}
                />
                {componentConfig.bookingPassportNextOfKinEmailEnabled && (
                  <TextField
                    label="Next of kin email address"
                    name="nextOfKinEmail"
                    value={nextOfKinEmail}
                    onChange={formChangeHandler}
                    customStyles={customTextFieldStyles}
                    disabled={!componentConfig.bookingPassportsUpdateEnabled}
                  />
                )}
                <SelectField
                  label="Next of kin relationship"
                  name="nextOfKinRelationship"
                  options={getValue(formDropdownConfig?.nokRelationship, [])}
                  value={nextOfKinRelationship}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  containerClassName="w-full"
                  disabled={!componentConfig.bookingPassportsUpdateEnabled}
                />
              </div>
            </>
          )}

          {passportType === "passenger" && (
            <>
              <SubSectionLabel className="mt-6" label="Insurance details" />
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="noInsurance"
                  name="noInsurance"
                  checked={noInsurance}
                  value={noInsurance}
                  onChange={handleNoInsuranceChange}
                  className="mr-2"
                />
                <label htmlFor="noInsurance">No insurance</label>
              </div>
              {noInsurance && isCruiseBooking && (
                <div className="text-red-500 text-sm mb-4">
                  Most cruise lines require you to have appropriate travel insurance before boarding. Please check the travel insurance requirements with your cruise operator.
                </div>
              )}
              <div className="flex flex-wrap gap-x-4 w-full justify-between">
                <TextField
                  label="Insurance company name"
                  name="insuranceCompanyName"
                  value={insuranceCompanyName}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled={noInsuranceState || !componentConfig.bookingPassportsUpdateEnabled}
                />
                <TextField
                  label="Insurance policy number"
                  name="insurancePolicyNumber"
                  value={insurancePolicyNumber}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled={noInsuranceState || !componentConfig.bookingPassportsUpdateEnabled}
                />
                <TextField
                  label="Insurance telephone number"
                  name="insuranceTelephone"
                  value={insuranceTelephone}
                  onChange={formChangeHandler}
                  customStyles={customTextFieldStyles}
                  disabled={noInsuranceState || !componentConfig.bookingPassportsUpdateEnabled}
                />
                {componentConfig.bookingPassportInsuranceValidFromTo && (
                  <>
                    <DateField
                      label="Insurance valid from"
                      name="insuranceValidFrom"
                      timestampValue={insuranceValidFrom}
                      onChange={formChangeHandler}
                      customStyles={customTextFieldStyles}
                      disabled={noInsuranceState || !componentConfig.bookingPassportsUpdateEnabled}
                    />
                    <DateField
                      label="Insurance valid to"
                      name="insuranceValidTo"
                      timestampValue={insuranceValidTo}
                      onChange={formChangeHandler}
                      customStyles={customTextFieldStyles}
                      disabled={noInsuranceState || !componentConfig.bookingPassportsUpdateEnabled}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {passportType === "client" && (
            <>
              <SubSectionLabel
                className="mt-6"
                label="Destination address (US only)"
              />
              <TextField
                label=""
                name="destinationAddress"
                value={destinationAddress}
                onChange={formChangeHandler}
              />
            </>
          )}
        </>
      }
      footer={
        <DefaultActionButtons
          onSecondaryButtonClick={closeModal}
          onPrimaryButtonClick={isNewPassport ? createPassport : () => updatePassport(passportType, bookingRefOrId)}
          isPrimaryButtonDisabled={!isObjectEmptyData(formErrorMessages)}
          hidePrimaryButton={passportType === "passenger" && !componentConfig.bookingPassportsUpdateEnabled}
        />
      }
    />
  );
}
